
import { useState } from "react";
import { Share2, Copy, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

const ShareListingsButton = () => {
  const [copied, setCopied] = useState(false);
  const [showManualCopy, setShowManualCopy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const { user, profile } = useAuth();

  // Fallback copy method using document.execCommand
  const fallbackCopyToClipboard = (text: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const result = document.execCommand('copy');
        document.body.removeChild(textArea);
        resolve(result);
      } catch (err) {
        document.body.removeChild(textArea);
        resolve(false);
      }
    });
  };

  const handleShareListings = async () => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "Unable to generate share link. Please try again.",
        variant: "destructive",
      });
      return;
    }

    const sellerHandle = profile?.profile_slug || user.id;
    const url = `https://godyspo.com/public/seller/${sellerHandle}`;
    setShareUrl(url);

    // Check if clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast({
          title: "Share link copied!",
          description: "Your listings link has been copied to clipboard.",
        });

        // Reset the copied state after 2 seconds
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.log("Clipboard API failed, trying fallback method:", err);
      }
    }

    // Try fallback method
    try {
      const success = await fallbackCopyToClipboard(url);
      if (success) {
        setCopied(true);
        toast({
          title: "Share link copied!",
          description: "Your listings link has been copied to clipboard.",
        });

        // Reset the copied state after 2 seconds
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (err) {
      console.log("Fallback copy method failed:", err);
    }

    // If all clipboard methods fail, show manual copy dialog
    setShowManualCopy(true);
    toast({
      title: "Manual copy required",
      description: "Please copy the link manually from the dialog that opened.",
      variant: "default",
    });
  };

  const handleManualCopy = async () => {
    try {
      // Try one more time with the clipboard API in the dialog context
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast({
          title: "Link copied!",
          description: "The share link has been copied to your clipboard.",
        });
        setShowManualCopy(false);
        return;
      }

      // Fallback for manual copy dialog
      const success = await fallbackCopyToClipboard(shareUrl);
      if (success) {
        toast({
          title: "Link copied!",
          description: "The share link has been copied to your clipboard.",
        });
        setShowManualCopy(false);
      }
    } catch (err) {
      console.error("Manual copy failed:", err);
      toast({
        title: "Copy failed",
        description: "Please select and copy the link manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <Button
        onClick={handleShareListings}
        variant="outline"
        className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black border-godyspo-gold"
      >
        {copied ? (
          <>
            <Check className="h-4 w-4 mr-2" />
            Copied!
          </>
        ) : (
          <>
            <Share2 className="h-4 w-4 mr-2" />
            Share Listings
          </>
        )}
      </Button>

      <Dialog open={showManualCopy} onOpenChange={setShowManualCopy}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Your Listings</DialogTitle>
            <DialogDescription>
              Copy this link to share your property listings with potential buyers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="grid flex-1 gap-2">
                <input
                  id="share-url"
                  value={shareUrl}
                  readOnly
                  className="w-full px-3 py-2 text-sm bg-muted rounded-md border border-input"
                  onClick={(e) => e.currentTarget.select()}
                />
              </div>
              <Button size="sm" onClick={handleManualCopy}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  window.open(shareUrl, '_blank');
                  toast({
                    title: "Link opened",
                    description: "Your listings page opened in a new tab.",
                  });
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Link
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowManualCopy(false)}
              >
                Close
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Tip: Click the input field above to select all text, then use Ctrl+C (or Cmd+C on Mac) to copy.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShareListingsButton;
